import type { Swiper } from 'swiper';

interface SlideAccessibilityOptions {
  swiper: Swiper;
  slides?: unknown[];
  useInert?: boolean;
  getSlideLabel?: (currentSlide: number, totalSlides: number) => string;
}

// Checks if a slide is the real active slide in loop mode (not a duplicate)
function isRealActiveSlideInLoop(slideEl: HTMLElement, originalIndex: number, realIndex: number): boolean {
  const isDuplicate = slideEl.classList.contains('swiper-slide-duplicate');
  return !isDuplicate && originalIndex === realIndex;
}

// Checks if a slide is visible in the viewport (for non-loop sliders)
function isVisibleSlide(slideEl: HTMLElement): boolean {
  const isVisible = slideEl.classList.contains('swiper-slide-visible');
  const isActive = slideEl.classList.contains('swiper-slide-active');
  return isVisible || isActive;
}

// Updates accessibility attributes for all slides in a swiper (Handles both loop mode and normal modes)
export const updateSlideAccessibility = ({
  swiper,
  slides,
  useInert = false,
  getSlideLabel,
}: SlideAccessibilityOptions) => {
  const slidesEl = swiper?.slides;
  if (!slidesEl || !slides) {
    return;
  }

  slidesEl.forEach((slideEl: HTMLElement, idx: number) => {
    const dataIndex = slideEl.getAttribute('data-swiper-slide-index');
    const isLoopMode = dataIndex !== null;

    // Determine slide index and label
    const slideIndex = isLoopMode ? Number(dataIndex) : idx;
    const slideLabel = getSlideLabel
      ? getSlideLabel(slideIndex + 1, slides.length)
      : `Slide ${slideIndex + 1} / ${slides.length}`; // Fallback - need to translate but should not be used unless getSlideLabel is not provided

    // Set role and label
    slideEl.setAttribute('role', 'group');
    slideEl.setAttribute('aria-label', slideLabel);

    // Determine if this slide should be accessible
    const shouldBeAccessible = isLoopMode
      ? isRealActiveSlideInLoop(slideEl, slideIndex, swiper.realIndex)
      : isVisibleSlide(slideEl);

    // Set accessibility attributes
    if (shouldBeAccessible) {
      // Visible slides
      slideEl.setAttribute('aria-hidden', 'false');
      slideEl.setAttribute('tabindex', '-1'); // Container itself not focusable, content inside is
      if (useInert) {
        slideEl.removeAttribute('inert');
      }
    } else {
      // Hidden slides
      slideEl.setAttribute('aria-hidden', 'true');
      slideEl.setAttribute('tabindex', '-1');
      if (useInert) {
        slideEl.setAttribute('inert', ''); // Prevents ALL focus on slide and its children
      }
    }
  });
};
